import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/app/AppShell";
import { api, DUCK_LOGO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Upload, File as FileIcon, Send, Calendar, Target, Sparkles, ClipboardCheck, CheckCircle2, MessageCircle, FileCheck2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PizzaTracker from "@/components/app/PizzaTracker";
import GoalCockpit from "@/components/app/GoalCockpit";
import { useGoal } from "@/context/GoalContext";
import ChangePasswordModal from "@/components/app/ChangePasswordModal";
import AuditTimeline from "@/components/app/AuditTimeline";
import DocPreviewDialog from "@/components/app/DocPreviewDialog";
import Avatar from "@/components/app/Avatar";
import PresenceDot from "@/components/app/PresenceDot";
import ClientScheduleCard from "@/components/app/ClientScheduleCard";
import CreditAnalysisPrompt from "@/components/app/CreditAnalysisPrompt";
import ProspectIntakeModal from "@/components/app/ProspectIntakeModal";
import DisputesSubmittedPrompt from "@/components/app/DisputesSubmittedPrompt";
import CreditBureauVaultCard from "@/components/app/CreditBureauVaultCard";
import { useRealtime } from "@/hooks/useRealtime";

export default function ClientDashboard() {
  const nav = useNavigate();
  const { goal } = useGoal();
  const [me, setMe] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [schedKey, setSchedKey] = useState(0);

  const load = async () => {
    const { data } = await api.get("/client/me");
    setMe(data);
    const m = await api.get("/client/messages");
    setMsgs(m.data);
  };
  useEffect(() => { load(); }, []);

  // "Set my goal" button in prospect intake modal fires cq:focus-goal.
  // Scroll to the Goal Cockpit and flash a soft amber ring so it's obvious.
  useEffect(() => {
    const handler = () => {
      // Wait a tick for modal to unmount
      setTimeout(() => {
        const el = document.querySelector('[data-testid^="goal-cockpit"]');
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("cq-focus-goal-ring");
        setTimeout(() => el.classList.remove("cq-focus-goal-ring"), 3600);
      }, 200);
    };
    window.addEventListener("cq:focus-goal", handler);
    return () => window.removeEventListener("cq:focus-goal", handler);
  }, []);

  // Real-time: refresh dashboard when admin pushes an update
  useRealtime((evt) => {
    if (!evt) return;
    if (evt.type === "client_updated") {
      load();
    } else if (evt.type === "notification" && (evt.data?.kind === "status_change" || evt.data?.kind?.startsWith("meeting_"))) {
      load();
    } else if (evt.type === "notification" && evt.data?.kind?.startsWith("schedule_")) {
      setSchedKey((k) => k + 1);
      load();
    }
  });

  if (!me) return <AppShell><div className="text-slate-500">Loading…</div></AppShell>;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/client/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — the team will review shortly.");
      load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    await api.post("/client/messages", { text: msgText });
    setMsgText("");
    const m = await api.get("/client/messages");
    setMsgs(m.data);
  };

  const isThirtyDay = me.effective_status === "Completed After 30 Days";

  return (
    <AppShell>
      <ProspectIntakeModal />
      <ChangePasswordModal forced />
      <CreditAnalysisPrompt status={me.effective_status} />
      <DisputesSubmittedPrompt
        status={me.effective_status}
        clientId={me.id}
        alreadyResponded={!!me.dispute_response_at}
      />

      <div className="cq-in">
        {/* Hero */}
        <div
          className="relative overflow-hidden rounded-3xl border border-slate-100 p-6 sm:p-10 mb-6 transition-all"
          style={goal?.has_goal ? { background: goal.theme.bg_gradient } : undefined}
        >
          <div className="cq-grain absolute inset-0" />
          <div className="relative flex items-start gap-4">
            <img src={DUCK_LOGO} alt="" className="hidden sm:block h-24 w-24 object-contain shrink-0 cq-waddle" />
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">
                {goal?.has_goal
                  ? <>Your journey · <span className="normal-case tracking-normal font-black">{goal.theme.emoji} {goal.theme.label}</span></>
                  : <>Your Credit Repair Journey</>}
              </p>
              <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">
                Hey {me.name.split(" ")[0]} 👋
              </h1>
              <p className="text-slate-600 mt-2 max-w-2xl">
                {goal?.has_goal
                  ? <>{goal.theme.tagline || "Every move you make now compounds toward your goal."} <span className="text-slate-500 italic">"{goal.goal_label}"</span></>
                  : <>Here's exactly where your file is right now — tracked like a pizza order, quacked with care.</>}
              </p>
            </div>
          </div>
        </div>

        {/* Pizza Tracker */}
        <Card id="tracker" className="p-6 rounded-2xl border-slate-100 mb-6 scroll-mt-24" data-testid="client-tracker-card">
          <PizzaTracker status={me.effective_status} startDate={me.start_date} />
        </Card>

        {/* Personal Goal Cockpit — sets theme, hero image, popups, and recs */}
        <div className="mb-6">
          <GoalCockpit />
        </div>

        {/* Scheduling — proposed times + confirmed */}
        <ClientScheduleCard key={schedKey} onChange={() => setSchedKey((k) => k + 1)} />

        {/* Marketing card — Upload real credit report */}
        <Card className="p-5 sm:p-6 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 mb-6 relative overflow-hidden" data-testid="upload-report-marketing">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-amber-300/30 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-lg shadow-amber-500/30 shrink-0">
              <FileCheck2 size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] uppercase tracking-widest font-bold mb-1">
                <Sparkles size={10} /> New · Free
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-black text-slate-900 leading-tight">Get your real funding data in 60 seconds</h3>
              <p className="text-sm text-slate-600 mt-1 max-w-xl">
                Upload your credit report PDF (from Credit Karma, IdentityIQ, Experian, or annualcreditreport.com) and we'll instantly unlock a personalized Funding Center powered by <span className="font-bold text-slate-900">your actual credit</span>.
              </p>
              <a
                href="/portal/funding?section=my_report"
                className="mt-3 inline-flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm px-4 py-2 rounded-xl transition"
                data-testid="upload-report-cta"
              >
                Upload my credit report <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </Card>

        <div className="mb-6">
          <CreditBureauVaultCard />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <InfoCard icon={Target} label="Current status" value={me.effective_status} accent={isThirtyDay ? "green" : "amber"} testid="current-status" />
          <InfoCard icon={Calendar} label="Started on" value={fmt(me.start_date)} accent="sky" testid="start-date" />
          <InfoCard icon={Sparkles} label="Est. completion" value={fmt(me.estimated_completion)} accent="sky" testid="est-completion" />
        </div>

        {/* Your Team */}
        {Array.isArray(me.team) && me.team.filter((t) => t.avatar_url).length > 0 && (
          <Card className="p-6 rounded-2xl border-slate-100 mb-6" data-testid="your-team-card">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Your team</p>
                <h3 className="font-display text-2xl font-bold text-slate-900 mt-1">The humans on your file</h3>
                <p className="text-sm text-slate-500 mt-1">Real specialists — not a bot, not a call center.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {me.team.filter((t) => t.avatar_url).slice(0, 4).map((t, i) => (
                <div
                  key={t.id || i}
                  className="group relative flex items-center gap-4 rounded-2xl border border-slate-100 bg-gradient-to-br from-white to-slate-50 p-4 hover:border-amber-200 hover:shadow-md transition"
                  data-testid={`team-member-${(t.first_name || t.name || "").toLowerCase()}`}
                >
                  <div className="relative">
                    <Avatar src={t.avatar_url} name={t.name} size="lg" tone="amber" ringed objectPosition="center 25%" />
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${
                        (() => {
                          if (!t.last_seen_at) return "bg-slate-300";
                          try { return (Date.now() - new Date(t.last_seen_at).getTime()) < 5*60*1000 ? "bg-emerald-500 animate-pulse" : "bg-slate-300"; }
                          catch { return "bg-slate-300"; }
                        })()
                      }`}
                      data-testid={`team-presence-${(t.first_name || "").toLowerCase()}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-black text-slate-900 text-lg leading-tight">{t.first_name || t.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">{t.title || (t.role === "super_admin" || t.role === "admin" ? "Manager" : "Specialist")}</p>
                    <PresenceDot lastSeenAt={t.last_seen_at} className="mt-1" />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => nav("/portal/messages", { state: { member: t } })}
                    className="bg-slate-900 hover:bg-amber-500 shrink-0"
                    data-testid={`team-message-${(t.first_name || "").toLowerCase()}`}
                  >
                    <MessageCircle size={14} className="mr-1" /> Message
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="p-6 rounded-2xl border-slate-100" data-testid="next-steps-card">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">What's next</p>
            <h3 className="font-display text-2xl font-bold text-slate-900 mt-1">Next Step</h3>
            <p className="text-slate-700 mt-3 text-lg leading-relaxed">{me.next_step}</p>
            {me.effective_status === "Credit Analysis" && (
              <Badge className="mt-4 bg-blue-100 text-blue-700">Under review — your specialist is analyzing your file</Badge>
            )}
            {(me.checklist || []).length > 0 && (
              <div className="mt-5 pt-5 border-t border-slate-100" data-testid="requested-docs-list">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardCheck size={16} className="text-sky-600" />
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Requested from you</p>
                </div>
                <ul className="space-y-2">
                  {me.checklist.map((c) => (
                    <li key={c.key} className="flex items-center gap-2 text-sm" data-testid={`requested-doc-${c.key}`}>
                      {c.received ? (
                        <><CheckCircle2 size={16} className="text-green-500" /><span className="line-through text-slate-400">{c.label}</span></>
                      ) : (
                        <><span className="w-4 h-4 rounded-full border-2 border-amber-400 inline-block shrink-0" /><span className="text-slate-800 font-medium">{c.label}</span></>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <Card className="p-6 rounded-2xl border-slate-100" data-testid="upload-card">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Documents</p>
            <h3 className="font-display text-2xl font-bold text-slate-900 mt-1">Upload for the team</h3>
            <label className="mt-3 block cursor-pointer rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center hover:border-amber-300 hover:bg-amber-50 transition" data-testid="client-upload-dropzone">
              <Upload className="mx-auto text-slate-400" size={22} />
              <p className="text-sm text-slate-600 mt-1">{uploading ? "Uploading…" : "Click or drop your document"}</p>
              <input type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="client-upload-input" />
            </label>
            <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
              {(me.documents || []).map((d) => (
                <button key={d.id} onClick={() => setPreviewDoc(d)} className="w-full text-left flex items-center gap-2 rounded-lg bg-white border border-slate-100 p-2 text-sm hover:bg-slate-50" data-testid={`client-doc-${d.id}`}>
                  <FileIcon size={14} className="text-slate-500" />
                  <span className="flex-1 truncate">{d.filename}</span>
                  <Badge variant="outline" className="text-[10px]">{d.source}</Badge>
                </button>
              ))}
              {(!me.documents || me.documents.length === 0) && <p className="text-sm text-slate-400">No documents yet.</p>}
            </div>
          </Card>
        </div>

        {/* Messages */}
        <Card id="messages" className="p-6 rounded-2xl border-slate-100 scroll-mt-24" data-testid="messages-card">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">Message the team</p>
          <h3 className="font-display text-2xl font-bold text-slate-900 mt-1">Chat</h3>
          <div className="mt-4 space-y-3 max-h-80 overflow-y-auto pr-1">
            {msgs.map((m) => {
              const mine = m.sender_role === "client";
              return (
                <div key={m.id} className={`flex gap-2 items-end ${mine ? "flex-row-reverse" : "flex-row"}`} data-testid={`message-${m.id}`}>
                  <Avatar
                    src={m.sender_avatar_url}
                    name={m.sender_name}
                    size="sm"
                    tone={mine ? "sky" : "amber"}
                    ringed
                  />
                  <div className={`max-w-[80%] rounded-2xl p-3 text-sm shadow-sm ${mine ? "bg-amber-100 text-amber-900 rounded-br-sm" : "bg-sky-100 text-sky-900 rounded-bl-sm"}`}>
                    <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                    <p className="text-[10px] opacity-70 mt-1 font-semibold">
                      {m.sender_name}
                      {m.sender_title && !mine ? ` · ${m.sender_title}` : ""}
                      {" · "}
                      {new Date(m.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              );
            })}
            {msgs.length === 0 && <p className="text-sm text-slate-400">No messages yet. Say hi 👋</p>}
          </div>
          <div className="mt-3 flex gap-2">
            <Input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Type a message to the team…" onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="client-msg-input" />
            <Button onClick={sendMsg} className="bg-amber-500 hover:bg-amber-600" data-testid="client-msg-send"><Send size={16} /></Button>
          </div>
        </Card>

        <div className="mt-6">
          <AuditTimeline endpoint="/client/timeline" refreshKey={me.updated_at} />
        </div>

        <DocPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(v) => !v && setPreviewDoc(null)} />
      </div>
    </AppShell>
  );
}

function InfoCard({ icon: Icon, label, value, accent = "amber", testid }) {
  const bg = { amber: "bg-amber-50 text-amber-700", sky: "bg-sky-50 text-sky-700", green: "bg-green-50 text-green-700" }[accent];
  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid={`infocard-${testid}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${bg}`}><Icon size={18} /></div>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{label}</p>
      <p className="font-display text-2xl font-black text-slate-900 mt-1" data-testid={testid}>{value}</p>
    </Card>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return "—"; }
}
